import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Heading,
  Icon,
  Input,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Text,
  useColorModeValue,
  useToast,
  VStack,
} from '@chakra-ui/react'
import QRCode from 'qrcode'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { FiSave, FiX } from 'react-icons/fi'
import api from '../../utils/api'
import { imprimirEtiquetas } from '../../utils/imprimirEtiqueta'
import { CONSELHOS, ESTADOS, Nacionalidade, NovoCadastroForm, Pessoa } from './types'

/*
  "Produção" e não "Staff e Produção": é o rótulo que a SBAM pediu na própria planilha
  ("criar um tipo de ingresso padrão, por exemplo Produção, dessa forma conseguimos
  imprimir em lote as etiquetas de todos"). Dois rótulos para a mesma equipe virariam dois
  grupos na impressão em lote, e "todos" viraria duas operações. É também o texto que cabe
  melhor na etiqueta de 82mm.
*/
const TIPOS_INGRESSO = [
  'Executivo',
  'Acesso VIP',
  'Produção',
  'Speakers - Credencial Palestrante',
]

const emptyForm = (): NovoCadastroForm => ({
  nome: '',
  cpf: '',
  email: '',
  celular: '',
  tipoIngresso: '',
  authorization: true,
  // Brasileiro é o padrão: 510 dos 513 inscritos têm CPF. O estrangeiro é a exceção
  // declarada, e é ela que troca a exigência de documento.
  nacionalidade: 'brasileiro',
  passaporteOuNif: '',
  empresa: '',
  dataNascimento: '',
  conselhoProfissional: '',
  registroProfissional: '',
  estadoUf: '',
})

const CadastroUsuario: React.FC<{
  pessoaEdit?: Pessoa
  onSave: (pessoa: Pessoa) => Promise<Pessoa | undefined>
  onCancel: () => void
}> = ({ pessoaEdit, onSave, onCancel }) => {
  const toast = useToast()
  const [isCheckinDialogOpen, setIsCheckinDialogOpen] = useState(false)
  const confirmRef = useRef<HTMLButtonElement>(null!)
  const [selectedPessoa, setSelectedPessoa] = useState<Pessoa | null>(null)

  const [form, setForm] = useState<NovoCadastroForm>(emptyForm())
  const [errors, setErrors] = useState<Record<string, string>>({})

  const isEditing = Boolean(pessoaEdit?.id)

  useEffect(() => {
    if (pessoaEdit) {
      const cf = pessoaEdit.customFields ?? {}
      setForm({
        nome:         pessoaEdit.nome ?? '',
        // O CNPJ tem chave própria na importação; sem lê-lo aqui, editar um inscrito de
        // empresa apagaria o documento dele ao salvar.
        cpf:          cf.cnpj || pessoaEdit.cpf || '',
        email:        pessoaEdit.email ?? '',
        celular:      cf.celular || pessoaEdit.telefone || '',
        tipoIngresso: cf.tipoIngresso ?? pessoaEdit.tipoIngresso ?? '',
        authorization: true,
        // Campos do briefing v2: vêm preenchidos da importação e precisam voltar ao
        // formulário, senão salvar uma edição os zeraria.
        nacionalidade:        cf.nacionalidade === 'estrangeiro' ? 'estrangeiro' : 'brasileiro',
        passaporteOuNif:      cf.passaporteOuNif ?? '',
        empresa:              cf.empresa ?? '',
        dataNascimento:       cf.dataNascimento ?? '',
        conselhoProfissional: cf.conselhoProfissional ?? '',
        registroProfissional: cf.registroProfissional ?? '',
        estadoUf:             cf.estadoUf ?? '',
      })
    }
  }, [pessoaEdit])

  const handleField = (key: keyof NovoCadastroForm, value: any) =>
    setForm((prev) => ({ ...prev, [key]: value }))

  /**
   * A lista do select, mais o valor já gravado quando ele vier de fora da lista.
   *
   * Um conselho que chega por planilha e não consta das opções abriria o campo vazio; como
   * ele é obrigatório, salvar a ficha substituiria o valor original sem ninguém perceber.
   * Acrescentar o que já está lá preserva o dado e mantém a lista fechada para cadastro novo.
   */
  /**
   * Estados da lista, mais o valor ja gravado quando ele vier de fora dela.
   *
   * A planilha trouxe "SP" solto em alguns registros; sem acrescentar, abrir a ficha
   * dessa pessoa mostraria o campo vazio e salvar apagaria o estado dela.
   */
  const opcoesEstado = useMemo(() => {
    const atual = form.estadoUf?.trim()
    const lista: string[] = [...ESTADOS]
    return atual && !lista.includes(atual) ? [...lista, atual] : lista
  }, [form.estadoUf])

  const opcoesConselho = useMemo(() => {
    const atual = form.conselhoProfissional?.trim()
    const lista: string[] = [...CONSELHOS]
    return atual && !lista.includes(atual) ? [...lista, atual] : lista
  }, [form.conselhoProfissional])

  // Obrigatórios do briefing v2, pág. 4. O documento é o único que muda de regra:
  // brasileiro exige CPF ou CNPJ, estrangeiro exige passaporte ou NIF no lugar.
  const validate = (): boolean => {
    const errs: Record<string, string> = {}
    const estrangeiro = form.nacionalidade === 'estrangeiro'

    if (!form.nome.trim())    errs.nome    = 'Nome é obrigatório'
    if (!form.email.trim())   errs.email   = 'E-mail é obrigatório'
    if (!form.celular.trim()) errs.celular = 'Telefone é obrigatório'
    if (!form.empresa.trim()) errs.empresa = 'Empresa é obrigatória'
    if (!form.dataNascimento.trim()) errs.dataNascimento = 'Data de nascimento é obrigatória'
    if (!form.conselhoProfissional.trim()) errs.conselhoProfissional = 'Conselho profissional é obrigatório'

    if (estrangeiro) {
      if (!form.passaporteOuNif.trim()) errs.passaporteOuNif = 'Informe passaporte ou NIF'
    } else {
      const doc = form.cpf.replace(/\D/g, '')
      if (!doc) errs.cpf = 'CPF ou CNPJ é obrigatório'
      else if (doc.length !== 11 && doc.length !== 14) errs.cpf = 'Informe 11 dígitos (CPF) ou 14 (CNPJ)'
    }

    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  const handleSave = async () => {
    if (!validate()) return

    const pessoaParaSalvar: Pessoa = {
      id:       pessoaEdit?.id,
      nome:     form.nome,
      email:    form.email,
      cpf:      form.cpf,
      telefone: form.celular,
      customFields: {
        ...(pessoaEdit?.customFields ?? {}),
        authorization: true,
        store:         'EVENTO',
        nomeNoCracha:  form.nome,
        // Guarda o que foi digitado, sem tirar os nao-digitos: numero estrangeiro tem
        // prefixo de pais e "+5511..." virava "5511..." — indistinguivel de um DDD daqui.
        celular:       form.celular.trim() || undefined,
        tipoIngresso:  form.tipoIngresso || undefined,
        // Os obrigatorios do briefing v2. Estavam sendo validados na tela e descartados
        // aqui: quem passava pelo balcao entrava sem conselho, sem empresa e sem
        // nascimento — e a etiqueta saia so com o nome, porque o conselho nunca chegou.
        nacionalidade:        form.nacionalidade,
        passaporteOuNif:      form.passaporteOuNif.trim() || undefined,
        dataNascimento:       form.dataNascimento || undefined,
        empresa:              form.empresa.trim() || undefined,
        conselhoProfissional: form.conselhoProfissional || undefined,
        registroProfissional: form.registroProfissional.trim() || undefined,
        estadoUf:             form.estadoUf || undefined,
      },
      registrationUid: pessoaEdit?.registrationUid,
    }

    const savedUser = await onSave(pessoaParaSalvar)
    if (savedUser) openCheckinDialog(savedUser)
  }

  const openCheckinDialog = (p: Pessoa) => {
    setSelectedPessoa(p)
    setIsCheckinDialogOpen(true)
  }

  const closeCheckinDialog = () => {
    setSelectedPessoa(null)
    setIsCheckinDialogOpen(false)
    onCancel()
  }

  /**
   * Etiqueta do recem-cadastrado, pelo mesmo caminho da consulta.
   *
   * Montava o HTML aqui, em 90x50mm, com nome/empresa/cargo. Ver utils/imprimirEtiqueta.
   */
  const printLabel = async (p: Pessoa) => {
    const code = p.credentialCode || p.registrationUid
    if (!code) return
    await imprimirEtiquetas([code])
  }

  const handleCheckinPrint = async () => {
    if (!selectedPessoa?.registrationUid) {
      toast({ title: 'Usuário sem registro de inscrição válido.', status: 'warning', duration: 3000, isClosable: true })
      return
    }
    try {
      await printLabel(selectedPessoa)
      await api.post('/checkin', { registration: selectedPessoa.registrationUid, room: null })
      toast({ title: 'Check-in + impressão realizados!', status: 'success', duration: 3000, isClosable: true })
    } catch {
      toast({ title: 'Erro ao registrar check-in.', status: 'error', duration: 3000, isClosable: true })
    } finally {
      closeCheckinDialog()
    }
  }

  const handleOnlyPrint = async () => {
    if (!selectedPessoa?.registrationUid) {
      toast({ title: 'Usuário sem registro de inscrição válido.', status: 'warning', duration: 3000, isClosable: true })
      return
    }
    try {
      await printLabel(selectedPessoa)
      toast({ title: 'Etiqueta enviada para impressão!', status: 'success', duration: 3000, isClosable: true })
    } catch {
      toast({ title: 'Erro ao imprimir etiqueta.', status: 'error', duration: 3000, isClosable: true })
    } finally {
      closeCheckinDialog()
    }
  }

  useEffect(() => { setErrors({}) }, [form])

  const cardBg = useColorModeValue('white', 'gray.800')

  return (
    <>
      {/* �??�?? Diálogo pós-salvar �??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�?? */}
      <AlertDialog isOpen={isCheckinDialogOpen} leastDestructiveRef={confirmRef} onClose={closeCheckinDialog}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">O que deseja fazer?</AlertDialogHeader>
            <AlertDialogBody>
              <Text as="span" fontWeight="bold">{selectedPessoa?.nome}</Text>
              <br />
              Escolha se deseja <strong>realizar o check-in e imprimir</strong> ou <strong>apenas imprimir</strong>.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={confirmRef} onClick={closeCheckinDialog}>Cancelar</Button>
              <Button colorScheme="blue" onClick={handleOnlyPrint} ml={3}>Apenas Imprimir</Button>
              <Button colorScheme="green" onClick={handleCheckinPrint} ml={3}>Check-in + Imprimir</Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* �??�?? Formulário �??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�?? */}
      <Box bg={cardBg} p={6} borderRadius="lg" boxShadow="lg" w="100%" mx="auto">
        <Heading size="lg" mb={6} textAlign="center" color={useColorModeValue('blue.600', 'blue.300')}>
          {isEditing ? 'Alterar Cadastro' : 'Novo Cadastro'}
        </Heading>

        <VStack spacing={4}>
          <FormControl isRequired isInvalid={!!errors.nome}>
            <FormLabel>Nome Completo</FormLabel>
            <Input
              value={form.nome}
              onChange={(e) => handleField('nome', e.target.value)}
              placeholder="Nome completo"
              autoComplete="off"
              maxLength={120}
            />
            <FormErrorMessage>{errors.nome}</FormErrorMessage>
          </FormControl>

          {/*
            A nacionalidade vem antes do documento de proposito: e ela que define qual
            documento sera exigido. Perguntar o CPF primeiro e so depois descobrir que a
            pessoa e estrangeira faz o atendente refazer o preenchimento na frente da fila.
          */}
          <FormControl isRequired>
            <FormLabel>Nacionalidade</FormLabel>
            <RadioGroup
              value={form.nacionalidade}
              onChange={(v) => handleField('nacionalidade', v as Nacionalidade)}
            >
              <Stack direction="row" spacing={6}>
                <Radio value="brasileiro">Brasileiro</Radio>
                <Radio value="estrangeiro">Estrangeiro</Radio>
              </Stack>
            </RadioGroup>
          </FormControl>

          {form.nacionalidade === 'brasileiro' ? (
            <FormControl isRequired isInvalid={!!errors.cpf}>
              <FormLabel>CPF ou CNPJ</FormLabel>
              <Input
                value={form.cpf}
                onChange={(e) => handleField('cpf', e.target.value)}
                placeholder="000.000.000-00 ou 00.000.000/0000-00"
                autoComplete="off"
                maxLength={30}
              />
              <FormErrorMessage>{errors.cpf}</FormErrorMessage>
            </FormControl>
          ) : (
            <>
              <FormControl isRequired isInvalid={!!errors.passaporteOuNif}>
                <FormLabel>Passaporte ou NIF</FormLabel>
                <Input
                  value={form.passaporteOuNif}
                  onChange={(e) => handleField('passaporteOuNif', e.target.value.toUpperCase())}
                  placeholder="Documento do país de origem"
                  autoComplete="off"
                  maxLength={40}
                />
                <FormErrorMessage>{errors.passaporteOuNif}</FormErrorMessage>
              </FormControl>

              <FormControl>
                <FormLabel>CPF ou CNPJ (se tiver)</FormLabel>
                <Input
                  value={form.cpf}
                  onChange={(e) => handleField('cpf', e.target.value)}
                  placeholder="opcional para estrangeiro"
                  autoComplete="off"
                  maxLength={30}
                />
              </FormControl>
            </>
          )}

          <FormControl isRequired isInvalid={!!errors.email}>
            <FormLabel>E-mail</FormLabel>
            <Input
              type="email"
              value={form.email}
              onChange={(e) => handleField('email', e.target.value)}
              placeholder="email@exemplo.com"
              maxLength={150}
            />
            <FormErrorMessage>{errors.email}</FormErrorMessage>
          </FormControl>

          {/*
            Sem máscara, de propósito: o campo é texto livre e aceita número de qualquer
            país. Impor "(00) 00000-0000" quebraria o inscrito estrangeiro, e o congresso
            tem participantes do Peru, Uruguai e Portugal.
          */}
          <FormControl isRequired isInvalid={!!errors.celular}>
            <FormLabel>Celular</FormLabel>
            <Input
              value={form.celular}
              onChange={(e) => handleField('celular', e.target.value)}
              placeholder={
                form.nacionalidade === 'estrangeiro'
                  ? '+51 999 999 999'
                  : '(00) 00000-0000'
              }
              maxLength={25}
            />
            {form.nacionalidade === 'estrangeiro' && (
              <FormHelperText>Inclua o código do país, começando com +.</FormHelperText>
            )}
            <FormErrorMessage>{errors.celular}</FormErrorMessage>
          </FormControl>

          <FormControl isRequired isInvalid={!!errors.dataNascimento}>
            <FormLabel>Data de nascimento</FormLabel>
            <Input
              type="date"
              value={form.dataNascimento}
              onChange={(e) => handleField('dataNascimento', e.target.value)}
              max={new Date().toISOString().slice(0, 10)}
            />
            <FormErrorMessage>{errors.dataNascimento}</FormErrorMessage>
          </FormControl>

          <FormControl isRequired isInvalid={!!errors.empresa}>
            <FormLabel>Empresa</FormLabel>
            <Input
              value={form.empresa}
              onChange={(e) => handleField('empresa', e.target.value)}
              placeholder="Empresa atual"
              autoComplete="off"
              maxLength={120}
            />
            <FormErrorMessage>{errors.empresa}</FormErrorMessage>
          </FormControl>

          {/*
            Estado e OPCIONAL, como o briefing define — por isso sem isRequired e com a
            opcao de deixar em branco. O campo ja existia no modelo e ja era gravado, mas
            nunca teve input: todo cadastro feito no balcao saia sem estado, e some do
            relatorio de origem do publico.
          */}
          <FormControl>
            <FormLabel>Estado</FormLabel>
            <Select
              value={form.estadoUf}
              onChange={(e) => handleField('estadoUf', e.target.value)}
              placeholder="Selecione o estado (opcional)"
            >
              {opcoesEstado.map((uf) => (
                <option key={uf} value={uf}>{uf}</option>
              ))}
            </Select>
          </FormControl>

          {/*
            Lista fechada, com os mesmos rotulos da planilha da SBAM. Texto livre aqui
            geraria "Medicina-CFM", "medicina cfm", "CFM" — e a regra de acesso restrito
            das palestras compara exatamente por este valor.

            `opcoesConselho` acrescenta o valor que ja esta gravado quando ele nao consta
            da lista. Sem isso o select abre vazio, o campo obrigatorio exige uma escolha, e
            salvar a ficha TROCA o valor que veio da planilha — foi o que aconteceria com os
            diretores da SBAM. A lista continua fechada para quem cadastra do zero.
          */}
          <FormControl isRequired isInvalid={!!errors.conselhoProfissional}>
            <FormLabel>Conselho profissional</FormLabel>
            <Select
              value={form.conselhoProfissional}
              onChange={(e) => handleField('conselhoProfissional', e.target.value)}
              placeholder="Selecione o conselho"
            >
              {opcoesConselho.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </Select>
            <FormErrorMessage>{errors.conselhoProfissional}</FormErrorMessage>
          </FormControl>

          <FormControl>
            <FormLabel>Registro profissional / UF</FormLabel>
            <Input
              value={form.registroProfissional}
              onChange={(e) => handleField('registroProfissional', e.target.value)}
              placeholder="00000 / UF"
              autoComplete="off"
              maxLength={40}
            />
          </FormControl>

          <FormControl>
            <FormLabel>Tipo do Ingresso</FormLabel>
            <Select
              placeholder="Selecione o tipo de ingresso"
              value={form.tipoIngresso}
              onChange={(e) => handleField('tipoIngresso', e.target.value)}
            >
              {TIPOS_INGRESSO.map((tipo) => (
                <option key={tipo} value={tipo}>{tipo}</option>
              ))}
            </Select>
          </FormControl>
        </VStack>

        {/* �??�?? Ações �??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�??�?? */}
        <Flex justify="space-between" mt={6} flexDirection={{ base: 'column', md: 'row' }} gap={2}>
          <Button leftIcon={<Icon as={FiX} />} variant="outline" onClick={onCancel}
            flex="1" colorScheme="red">
            Cancelar
          </Button>
          <Button leftIcon={<Icon as={FiSave} />} colorScheme="blue" onClick={handleSave}
            flex="1" _hover={{ bg: 'blue.600' }}>
            Salvar
          </Button>
        </Flex>
      </Box>
    </>
  )
}

export default CadastroUsuario
